import { ButtonPrimary, ButtonWorkspaceDropdown } from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { AddOns, BillingCycle, Licenses, SubscriptionsType } from "@nova/constants"
import { useCallback, useMemo, useState } from "react"
import { LicenseDefinitions, AddOnDefinitions, LicenseDefinition } from "../module"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectUserEmail, selectUserID } from "user/profile/selectors"
import { selectUserFullName } from "features/entity-caching/members/UserManagementSlice"
import { useTranslation } from "@nova/use-translation"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { formatPrice } from "@nova/util-strings"
import classNames from "classnames"
import { useAsyncCallback } from "@nova/use-async-callback"
import { hideGlobalModal } from "runlevel/actions"

type SubscriptionsCartItem = {
    definition: LicenseDefinition
    quantity: number
}

const defaultCart = {
    licenses: { lite: undefined, essentials: undefined, pro: undefined },
    addOns: { colorfinder: undefined, hotspots: undefined, liveVideo: undefined },
}
function getNewDefaultCart() {
    return {
        licenses: { ...defaultCart.licenses },
        addOns: { ...defaultCart.addOns },
    }
}

type ShoppingCart = {
    licenses: Record<Licenses, SubscriptionsCartItem | undefined>
    addOns: Record<AddOns, SubscriptionsCartItem | undefined>
}
const createStripeCheckoutSession = async (context: {
    email: string
    userId: string
    fullName: string
    redirectUrl: string
    billingCycle: BillingCycle
    shoppingCart: ShoppingCart
}) => {
    const { email, userId, fullName, redirectUrl, shoppingCart, billingCycle } = context

    const quantities: Record<SubscriptionsType, number> = {
        lite: 0,
        essentials: 0,
        pro: 0,
        hotspots: 0,
        colorfinder: 0,
        liveVideo: 0,
    }
    Object.values({ ...shoppingCart.licenses, ...shoppingCart.addOns }).forEach((item) => {
        if (!item || item.quantity === 0) return
        quantities[item?.definition.type] = item?.quantity
    })

    const res = await fetch(
        `/api/stripe/stripe-create-checkout?email=${encodeURIComponent(
            email
        )}&fullName=${fullName}&redirectUrl=${redirectUrl}&userId=${userId}&billingCycle=${billingCycle}`,
        {
            method: "POST",
            body: JSON.stringify(quantities),
        }
    ).then((r) => r.json())

    if (!res.url) return false
    window.location.href = res.url
    return true
}

export const LicenseCheckoutModal = () => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const email = useAppSelector(selectUserEmail)
    const userId = useAppSelector(selectUserID)
    const fullName = useAppSelector(selectUserFullName())

    const [subscriptionsCart, setSubcriptionsCart] = useState<ShoppingCart>(getNewDefaultCart())

    const [isAnnual, setIsAnnual] = useState(true)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)

    const handleAddToCart = useCallback(
        (definition: LicenseDefinition, isLicense: boolean) => {
            const { licenses, addOns } = subscriptionsCart
            const newLicenses = licenses
            const newAddOns = addOns
            if (isLicense) {
                const type = definition.type as Licenses
                newLicenses[type] = {
                    definition,
                    quantity: 1,
                }
            } else {
                const type = definition.type as AddOns
                newAddOns[type] = {
                    definition,
                    quantity: 1,
                }
            }
            setSubcriptionsCart({ licenses: newLicenses, addOns: newAddOns })
        },
        [subscriptionsCart]
    )

    const handleChangeQuantity = useCallback(
        (definition: LicenseDefinition, isLicense: boolean, newQuantity: number) => {
            const { licenses, addOns } = subscriptionsCart
            if (isLicense) {
                const type = definition.type as Licenses
                licenses[type] = { definition, quantity: newQuantity }
            } else {
                const type = definition.type as AddOns
                addOns[type] = { definition, quantity: newQuantity }
            }
            setSubcriptionsCart({ licenses, addOns })
        },
        [subscriptionsCart]
    )

    const handleResetCart = useCallback(() => {
        setSubcriptionsCart(getNewDefaultCart())
    }, [])

    const { handleCheckout, loading: isLoading } = useAsyncCallback(
        {
            handleCheckout: async () => {
                if (!email || !userId || !fullName) return
                const succeeded = await createStripeCheckoutSession({
                    email,
                    userId,
                    fullName,
                    redirectUrl: encodeURIComponent(window.location.href),
                    shoppingCart: subscriptionsCart,
                    billingCycle: isAnnual ? BillingCycle.Year : BillingCycle.Month,
                })

                if (!succeeded) {
                    dispatch(
                        setGlobalNotificationContext({
                            status: GlobalNotificationStatus.error,
                            message: "Failed to redirect to checkout page. Please contact support.",
                            timeoutInMs: 3000,
                        })
                    )
                }
            },
        },
        [email, userId, fullName, subscriptionsCart, isAnnual, dispatch]
    )

    const subtotal = useMemo(() => {
        const allItems = [
            ...Object.values(subscriptionsCart.addOns),
            ...Object.values(subscriptionsCart.licenses),
        ]
        let totalPrice = 0
        allItems.forEach((item) => {
            if (!item || (!isAnnual && item?.definition.hasMonthlyPaywall == false)) return
            const priceToAdd = isAnnual
                ? item.definition.pricePerYear
                : item.definition.pricePerMonth
            for (let i = 0; i < item.quantity; i++) {
                totalPrice += priceToAdd
            }
        })
        return totalPrice
    }, [subscriptionsCart, isAnnual])
    const isCartEmpty = subtotal === 0
    const cartHasLicenses = useMemo(
        () =>
            Object.values(subscriptionsCart.licenses).some(
                (license) => !!license && license.quantity > 0
            ),
        [subscriptionsCart]
    )
    const cartHasAddOns = useMemo(
        () =>
            Object.values(subscriptionsCart.addOns).some((addOn) => !!addOn && addOn.quantity > 0),
        [subscriptionsCart]
    )
    return (
        <HighPriorityNovaModalContainer
            title="Select your Subscriptions"
            closeModal={() => dispatch(hideGlobalModal())}
            className="!max-w-[74.75rem]"
            hideTitleUnderline={true}
            titleMarginClassName="mt-6"
        >
            <div className="flex gap-5 mt-0.5">
                <div className="absolute right-[19.875rem] top-6 flex items-center gap-2.5 z-50">
                    <h5>{t("Billing Frequency:")}</h5>
                    <ButtonWorkspaceDropdown
                        label={isAnnual ? "Annual" : "Monthly"}
                        menuOpen={isDropdownOpen}
                        setMenuOpen={setIsDropdownOpen}
                        widthClass="w-[6.875rem]"
                        dropdownButtons={[
                            { label: "Annual", onClick: () => setIsAnnual(true) },
                            { label: "Monthly", onClick: () => setIsAnnual(false) },
                        ]}
                    />
                </div>
                <ScrollableArea className="max-h-[calc(100vh-15.625rem)] flex flex-col pr-3 relative">
                    {Object.values(LicenseDefinitions).map((def) => {
                        if (def.type === Licenses.Lite) return
                        return (
                            <CheckoutSection
                                key={def.type}
                                definition={def}
                                isAnnual={isAnnual}
                                handleAddToCart={() => handleAddToCart(def, true)}
                                handleChangeQuantity={(quantity) =>
                                    handleChangeQuantity(def, true, quantity)
                                }
                                quantity={
                                    subscriptionsCart.licenses[def.type as Licenses]?.quantity || 0
                                }
                            />
                        )
                    })}
                    <div className="w-full border-b border-gray-600 my-4" />
                    <small className="tracking-[0.075rem]">{t("ADD-ONS")}</small>
                    {Object.values(AddOnDefinitions).map((def) => {
                        if (
                            def.type === Licenses.Lite ||
                            (!isAnnual && def.hasMonthlyPaywall === false)
                        )
                            return
                        return (
                            <CheckoutSection
                                key={def.type}
                                definition={def}
                                isAnnual={isAnnual}
                                handleAddToCart={() => handleAddToCart(def, false)}
                                handleChangeQuantity={(quantity) =>
                                    handleChangeQuantity(def, false, quantity)
                                }
                                quantity={
                                    subscriptionsCart.addOns[def.type as AddOns]?.quantity || 0
                                }
                            />
                        )
                    })}
                </ScrollableArea>

                {/** checkout summary */}
                <div className="w-[19.375rem] h-max box-border p-4 border border-gray-700 rounded-xl flex flex-col">
                    <h4>{t("Summary")}</h4>
                    <div className="w-full  border-b border-gray-700 mt-2.5 mb-4" />
                    {!isCartEmpty && (
                        <>
                            {cartHasLicenses && (
                                <div className="flex justify-between">
                                    <small className="text-gray-400">{t("LICENSES")}</small>
                                    <small className="text-gray-400 self-end">
                                        {t(isAnnual ? "Annual" : "Monthly")}
                                    </small>
                                </div>
                            )}
                            {Object.values(subscriptionsCart.licenses).map((license, idx) => {
                                if (license)
                                    return (
                                        <SummaryItem key={idx} {...license} isAnnual={isAnnual} />
                                    )
                            })}
                            {cartHasAddOns && (
                                <div
                                    className={classNames("flex justify-between", {
                                        "mt-8": cartHasLicenses,
                                    })}
                                >
                                    <small className="text-gray-400">{t("ADD-ONS")}</small>
                                    <small className="text-gray-400 self-end">
                                        {t(isAnnual ? "Annual" : "Monthly")}
                                    </small>
                                </div>
                            )}
                            {Object.values(subscriptionsCart.addOns).map((addOn, idx) => {
                                if (!isAnnual && addOn?.definition.hasMonthlyPaywall == false)
                                    return
                                if (addOn)
                                    return <SummaryItem key={idx} {...addOn} isAnnual={isAnnual} />
                            })}
                        </>
                    )}
                    <div className="w-full border-b border-gray-700 my-4 pb-2" />
                    <div className="flex justify-between items-center">
                        <h5>{t("Subtotal")}</h5>
                        {!isCartEmpty && (
                            <h2 className="font-medium mono">{t(formatPrice(subtotal))}</h2>
                        )}
                    </div>
                    {!isCartEmpty && (
                        <div className="text-right flex flex-col">
                            <small className="text-gray-400">{t("due today - USD")}</small>
                            <small className="text-gray-400">
                                {t("taxes calculated at checkout")}
                            </small>
                        </div>
                    )}
                    <ButtonPrimary
                        title="Proceed to Checkout"
                        className={classNames("my-4 w-full relative", {
                            "mb-0": !isCartEmpty,
                        })}
                        onClick={handleCheckout}
                        RightIcon={Icons.ArrowRight}
                        disabled={isCartEmpty}
                        isLoading={isLoading}
                    />
                    {!isCartEmpty && (
                        <>
                            <small
                                className="text-gray-100 hover:text-gray-0 mt-3 mb-5 w-full text-center cursor-pointer"
                                onClick={handleResetCart}
                            >
                                Clear cart
                            </small>
                            <div className="text-gray-100">
                                <small>
                                    {t("Need help or exploring manned aircraft integrations?")}
                                </small>
                                <small
                                    className="text-blue-500 underline text-xs ml-1 cursor-pointer"
                                    onClick={() => window.open("mailto:help@mapnova.com", "_blank")}
                                >
                                    {t("Contact sales.")}
                                </small>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </HighPriorityNovaModalContainer>
    )
}

const SummaryItem = ({
    definition,
    quantity,
    isAnnual,
}: {
    definition: LicenseDefinition
    quantity: number
    isAnnual: boolean
}) => {
    const { t } = useTranslation()
    if (quantity === 0) return null
    const basePrice = isAnnual ? definition.pricePerYear : definition.pricePerMonth
    const totalPrice = basePrice * quantity
    return (
        <div>
            <div className="flex items-center justify-between">
                <h5>{t(`${quantity}x ${definition.title}`)}</h5>
                <h5 className="mono">{t(formatPrice(totalPrice))}</h5>
            </div>
        </div>
    )
}

const CheckoutSection = ({
    definition,
    isAnnual,
    quantity,
    handleAddToCart,
    handleChangeQuantity,
}: {
    definition: LicenseDefinition
    isAnnual: boolean
    quantity: number
    handleAddToCart: () => void
    handleChangeQuantity: (newQuantity: number) => void
}) => {
    const { t } = useTranslation()
    const { title, subtitle, pricePerMonth, pricePerYear, perks, checkoutIcon: Icon } = definition

    const middle = Math.ceil(perks.length / 2)
    const firstHalf = perks.slice(0, middle)
    const secondHalf = perks.slice(middle)

    const priceToUse = isAnnual ? pricePerYear / 12 : pricePerMonth
    return (
        <>
            <div className="w-full border-b border-gray-600 my-4" />
            <div className="flex gap-7 my-7">
                <div className="min-w-[3.25rem]">
                    <Icon
                        className={
                            //scale factors for 24px -> 52px
                            "scale-[2.1667] translate-x-[58.335%] translate-y-[58.335%]"
                        }
                    />
                </div>
                <div className="license-checkout-grid gap-5">
                    <div>
                        <h2 className="font-medium">{t(title)}</h2>
                        <h5 className="text-gray-300 mt-0.5">{t(subtitle)}</h5>
                        <h5 className="mt-5">{t("You'll receive:")}</h5>
                        <ul className="list-disc pl-5 mt-0.5">
                            {firstHalf.map((perk, idx) => (
                                <li key={idx} className="text-gray-300">
                                    <h5>{t(perk)}</h5>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="mt-[0.313rem]">
                        <>
                            <div className="flex">
                                <h2 className="font-medium mono">{t(formatPrice(priceToUse))}</h2>
                                <small className="self-end mb-0.5">{t("/mo")}</small>
                            </div>
                            <div className="text-gray-300">
                                {isAnnual && (
                                    <>
                                        <small>{t(`USD - billed annually `)}</small>
                                        <small className="mono">{formatPrice(pricePerYear)}</small>
                                    </>
                                )}
                                {!isAnnual && <small>{t(`USD - billed monthly`)}</small>}
                            </div>
                        </>
                        <ul className="mt-[2.484rem] list-disc pl-5">
                            {secondHalf.map((perk, idx) => (
                                <li key={idx} className="text-gray-300">
                                    <h5>{t(perk)}</h5>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* controls */}
                    <div className="flex flex-col gap-5 mr-[3.75rem] mt-[0.875rem]">
                        {quantity <= 0 ? (
                            <ButtonPrimary
                                title="Add Subscription"
                                onClick={() => {
                                    handleAddToCart()
                                }}
                                className="w-[9.75rem] relative"
                                LeftIcon={Icons.ShoppingCart}
                            />
                        ) : (
                            <div className="w-[9.75rem]">
                                <div className="h-8 flex items-center border border-gray-500 rounded-lg w-max px-1">
                                    <Icons.Minus
                                        className="cursor-pointer"
                                        onClick={() => handleChangeQuantity(quantity - 1)}
                                    />
                                    <h5 className="px-3 mx-1 border-x border-gray-500 select-none">
                                        {quantity}
                                    </h5>
                                    <Icons.Plus
                                        className="cursor-pointer"
                                        onClick={() => handleChangeQuantity(quantity + 1)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}
